import { useQuery } from '@tanstack/react-query'

import { get } from '@/lib/api'

import type { WorkflowStage } from '@/components/pm/gantt/types'

// ==================== Types ====================

export interface AnalyticsFilters {
  projectId?: string
  teamId?: string
  startDate?: string
  endDate?: string
}

export interface StageStatistics {
  stage: WorkflowStage
  totalTasks: number
  completedTasks: number
  averageDurationHours: number
  estimatedDurationHours: number
  rollbackCount: number
  rollbackRate: number
  isBottleneck: boolean
  isHighRollbackRate: boolean
}

export type BottleneckReason = 'slow' | 'high_rollback' | 'both'

export interface BottleneckAnalysis {
  stage: WorkflowStage
  reason: BottleneckReason
  averageDurationHours: number
  estimatedDurationHours: number
  durationRatio: number
  rollbackRate: number
  recommendations: string[]
}

export interface StageAccuracy {
  stage: WorkflowStage
  accuracy: number
}

export interface MemberAccuracy {
  employeeId: string
  employeeName: string
  accuracy: number
}

export interface AccuracyTrend {
  date: string
  accuracy: number
}

export interface EstimationAccuracy {
  overall: number
  byStage: StageAccuracy[]
  byMember: MemberAccuracy[]
  trend: AccuracyTrend[]
}

export type EfficiencyTrend = 'improving' | 'stable' | 'declining'

export interface TeamEfficiency {
  teamId: string
  teamName: string
  averageCompletionDays: number
  rollbackRate: number
  taskCount: number
  rank: number
  trend: EfficiencyTrend
}

export interface TrendDataPoint {
  date: string
  completedCount: number
  totalCount: number
  completionRate: number
}

// ==================== Query Keys ====================

export const workflowAnalyticsKeys = {
  all: ['workflow-analytics'] as const,
  stageStatistics: (filters: AnalyticsFilters) =>
    [...workflowAnalyticsKeys.all, 'stage-statistics', filters] as const,
  bottlenecks: (filters: AnalyticsFilters) =>
    [...workflowAnalyticsKeys.all, 'bottlenecks', filters] as const,
  estimationAccuracy: (filters: AnalyticsFilters) =>
    [...workflowAnalyticsKeys.all, 'estimation-accuracy', filters] as const,
  teamEfficiency: (filters: AnalyticsFilters) =>
    [...workflowAnalyticsKeys.all, 'team-efficiency', filters] as const,
  completionTrend: (filters: AnalyticsFilters, days: number) =>
    [...workflowAnalyticsKeys.all, 'completion-trend', filters, days] as const,
}

// ==================== Hooks ====================

/**
 * 获取阶段统计
 * Requirements: 3.1, 4.2, 5.1
 */
export function useStageStatistics(filters: AnalyticsFilters = {}) {
  return useQuery({
    queryKey: workflowAnalyticsKeys.stageStatistics(filters),
    queryFn: () =>
      get<StageStatistics[]>(
        '/pm/workflow-analytics/stage-statistics',
        filters as Record<string, unknown>
      ),
  })
}

/**
 * 获取瓶颈分析
 * Requirements: 3.4, 3.5, 3.8
 */
export function useBottleneckAnalysis(filters: AnalyticsFilters = {}) {
  return useQuery({
    queryKey: workflowAnalyticsKeys.bottlenecks(filters),
    queryFn: () =>
      get<BottleneckAnalysis[]>(
        '/pm/workflow-analytics/bottlenecks',
        filters as Record<string, unknown>
      ),
  })
}

/**
 * 获取估算准确率
 * Requirements: 4.1, 4.2, 4.3, 4.4
 */
export function useEstimationAccuracy(filters: AnalyticsFilters = {}) {
  return useQuery({
    queryKey: workflowAnalyticsKeys.estimationAccuracy(filters),
    queryFn: () =>
      get<EstimationAccuracy>(
        '/pm/workflow-analytics/estimation-accuracy',
        filters as Record<string, unknown>
      ),
  })
}

/**
 * 获取团队效率
 * Requirements: 5.1, 5.2, 5.3, 5.4
 */
export function useTeamEfficiency(filters: AnalyticsFilters = {}) {
  return useQuery({
    queryKey: workflowAnalyticsKeys.teamEfficiency(filters),
    queryFn: () =>
      get<TeamEfficiency[]>(
        '/pm/workflow-analytics/team-efficiency',
        filters as Record<string, unknown>
      ),
  })
}

/**
 * 获取完成率趋势
 * Requirements: 3.7
 */
export function useCompletionTrend(filters: AnalyticsFilters = {}, days: number = 30) {
  return useQuery({
    queryKey: workflowAnalyticsKeys.completionTrend(filters, days),
    queryFn: () =>
      get<TrendDataPoint[]>('/pm/workflow-analytics/completion-trend', {
        ...filters,
        days,
      } as Record<string, unknown>),
  })
}

/**
 * 导出报表
 * Requirements: 3.9, 4.5, 5.5
 */
export function useExportReport() {
  return async (filters: AnalyticsFilters, format: 'csv' | 'json' = 'json') => {
    const response = await get<string>('/pm/workflow-analytics/export', {
      ...filters,
      format,
    } as Record<string, unknown>)
    return response
  }
}

// ==================== Display Helpers ====================

/**
 * 获取瓶颈原因显示名称
 */
export function getBottleneckReasonDisplayName(reason: BottleneckReason): string {
  const names: Record<BottleneckReason, string> = {
    slow: '耗时过长',
    high_rollback: '回退率高',
    both: '耗时过长且回退率高',
  }
  return names[reason]
}

/**
 * 获取效率趋势显示名称
 */
export function getEfficiencyTrendDisplayName(trend: EfficiencyTrend): string {
  const names: Record<EfficiencyTrend, string> = {
    improving: '上升',
    stable: '稳定',
    declining: '下降',
  }
  return names[trend]
}

/**
 * 获取效率趋势颜色
 */
export function getEfficiencyTrendColor(trend: EfficiencyTrend): string {
  const colors: Record<EfficiencyTrend, string> = {
    improving: 'text-green-600',
    stable: 'text-gray-600',
    declining: 'text-red-600',
  }
  return colors[trend]
}

/**
 * 格式化百分比
 */
export function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

/**
 * 格式化小时数
 */
export function formatHours(hours: number): string {
  if (hours < 1) {
    return `${Math.round(hours * 60)}分钟`
  }
  return `${hours.toFixed(1)}小时`
}
